import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import moment from 'moment';
import i18n from 'i18n-js';
import { buildUrl, getLMKBUrl } from 'utils';
import { ADMIN_ROLES } from 'constants';
import { NavLink, Link } from 'react-router-dom';
import { GuidingModal } from 'components/Modals';
import { UserOrgsDropdown } from './UserOrgsDropdown';
import NotificationsIcon from '@material-ui/icons/Notifications';
import HelpIcon from '@material-ui/icons/Help';
import { OrganizationsActions } from 'state/modules/organizations';
import logo from 'assets/img/themes/launchmobility/logo-sidebar.png';
import SharingAbility from '../SharingAbility';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Tooltip,
  createSvgIcon,
} from '@material-ui/core';

//TODO: Replace with AutoStories once we Have MaterialV5
const LMKBIcon = createSvgIcon(
  <path d="m19 1-5 5v11l5-4.5V1zM1 6v14.65c0 .25.25.5.5.5.1 0 .15-.05.25-.05C3.1 20.45 5.05 20 6.5 20c1.95 0 4.05.4 5.5 1.5V6c-1.45-1.1-3.55-1.5-5.5-1.5S2.45 4.9 1 6zm22 13.5V6c-.6-.45-1.25-.75-2-1v13.5c-1.1-.35-2.3-.5-3.5-.5-1.7 0-4.15.65-5.5 1.5v2c1.35-.85 3.8-1.5 5.5-1.5 1.65 0 3.35.3 4.75 1.05.1.05.15.05.25.05.25 0 .5-.25.5-.5v-1.1z" />,
  'LMKB Icon'
);

const Header = ({ classes }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const loading = useSelector((state) => state.organizations.loading);
  const organization = useSelector((state) => state.organizations.organization);
  const localeFormat = useSelector((state) => state.organizations.localeFormat);
  const [openLMKModal, setOpenLMKModal] = useState(false);
  const services = useSelector((state) => state.auth?.organizations?.list) || [];
  const [date, setDate] = useState(moment().format(`${localeFormat} hh:mm A (UTC Z) z`));
  const guidingModalOpen = useSelector((state) => state.userSettings?.guidingModalOpen);
  const walkthroughActive = useSelector(
    (state) => state.config?.settings?.integrations?.settings?.walkthrough?.active || false
  );
  const preferences = useSelector((state) =>
    state?.auth?.user?.preferences === null
      ? { walkthrough_permanent: true }
      : state?.auth?.user?.preferences
  );
  const walkthroughPermanent = preferences?.walkthrough_permanent;

  useEffect(() => {
    if (user?.organization?.id && organization?.id !== user?.organization?.id) {
      dispatch(OrganizationsActions.fetchOrganizationById(user?.organization?.id));
    }
  }, [user, dispatch]);

  useEffect(() => {
    if ((guidingModalOpen && walkthroughPermanent) || openLMKModal) {
      setOpenLMKModal(true);
    }
  }, [guidingModalOpen, walkthroughPermanent, openLMKModal]);

  useEffect(() => {
    let interval;
    if (process.env.NODE_ENV !== 'test') {
      // This effect is disabled for test env as it causes multiple renders
      interval = setInterval(() => {
        setDate(moment().tz(moment.tz.guess()).format(`${localeFormat} hh:mm A (UTC Z) z`));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [localeFormat]);

  const handleOpenLMKModal = () => setOpenLMKModal((prevValue) => !prevValue);
  return (
    <AppBar color="inherit" position="fixed" className={clsx(classes.appBar)}>
      <Toolbar className={classes.toolbar}>
        <Box display="flex" alignItems="center" className={classes.locoContainer}>
          {(organization?.id || !loading) && (
            <img
              style={{
                maxHeight: 50,
                objectFit: 'cover',
              }}
              src={organization?.logo_url || logo}
              alt="logo-collapsed"
              className={classes.logo}
            />
          )}
        </Box>
        <Box display="flex">{services?.length > 0 ? <UserOrgsDropdown /> : null}</Box>
        <Box display="flex" alignItems="center" justifyContent="flex-end" width="100%">
          <Box mr={1}>
            <Typography variant="body2" noWrap className={classes.date}>
              {date.toString()}
            </Typography>
          </Box>
          {ADMIN_ROLES.includes(user?.role) && walkthroughActive && (
            <IconButton
              aria-label="notification-bell-button"
              size="medium"
              onClick={() => {
                handleOpenLMKModal();
              }}
              className={classes.notificationsIcon}
              target="_blank"
              rel="noopener noreferrer"
            >
              <HelpIcon />
            </IconButton>
          )}
          <Tooltip title={i18n.t('common.lmkb_tooltip')}>
            <IconButton
              aria-label="notification-bell-button"
              size="medium"
              href={getLMKBUrl()}
              className={classes.notificationsIcon}
              target="_blank"
              rel="noopener noreferrer"
            >
              <LMKBIcon />
            </IconButton>
          </Tooltip>
          <SharingAbility subject={['FAAS']} allowed={false}>
            <IconButton
              aria-label="notification-bell-button"
              size="medium"
              to={buildUrl('/notifications')}
              component={Link}
              className={classes.notificationsIcon}
            >
              <NotificationsIcon />
            </IconButton>
          </SharingAbility>
          <Box display="flex" alignItems="center" ml={1}>
            <NavLink to={buildUrl('/account')}>
              <Typography component="span" className={classes.name} data-cy="account">
                {user?.first_name} {user?.last_name}
              </Typography>
            </NavLink>
          </Box>
        </Box>
      </Toolbar>
      {openLMKModal && ADMIN_ROLES.includes(user?.role) && walkthroughActive && (
        <GuidingModal maxWidth={'990px'} openModal={() => handleOpenLMKModal()} />
      )}
    </AppBar>
  );
};

export default Header;
